import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Google Search Console → Backlink Dashboard importer.
 *
 * Note on automation: Google's Search Console API (webmasters/v3 + URL Inspection)
 * does NOT expose the Links report. Referring domains and anchor text can only be
 * obtained from the Links report export in the GSC UI. This panel ingests those
 * exports (CSV or the sheets from the XLSX exported as CSV) and merges them into
 * the dashboard, deduping by domain / anchor so repeat imports stay clean.
 */

export interface GSCReferringDomain {
  domain: string;
  links: number;
  firstSeen: string; // ISO date of first import
  lastSeen: string; // ISO date of most recent import
  status: "NEW" | "TO CONTACT" | "REVIEWED" | "OUTREACH" | "IGNORED";
  /** Why the auto-classifier assigned the initial status. */
  rule?: string;
}

export interface GSCAnchor {
  anchor: string;
  count: number;
  lastSeen: string;
}

interface Store {
  domains: Record<string, GSCReferringDomain>;
  anchors: Record<string, GSCAnchor>;
  lastImport: string | null;
  property: string;
}

const STORAGE_KEY = "avt_gsc_backlinks_v1";

const EMPTY: Store = { domains: {}, anchors: {}, lastImport: null, property: "" };

const STATUS_CYCLE: Record<GSCReferringDomain["status"], GSCReferringDomain["status"]> = {
  NEW: "TO CONTACT",
  "TO CONTACT": "REVIEWED",
  REVIEWED: "OUTREACH",
  OUTREACH: "IGNORED",
  IGNORED: "NEW",
};

/* ------------------------------------------------------------------ */
/* Strategy-pillar auto-classification rules                           */
/* ------------------------------------------------------------------ */

/** Toxic PBN / link-scheme domains (mirrors public/disavow.txt). */
const TOXIC_DOMAINS = [
  "gncrklmlimd.blogspot.com",
  "8coint.com",
  "cindylaup.com",
  "toplikevideo.com",
  "analyticshaven.top",
  "anchorurl.cloud",
  "atomizelink.icu",
  "buzzshrink.website",
  "digitalseotool.com",
];

/** TLDs typical of throwaway spam networks. */
const SPAM_TLDS = /\.(icu|top|cloud|website|xyz|click|link|buzz|monster|tk|ml|ga|cf|gq)$/i;

/**
 * Strategy pillars from the backlink dashboard. A brand-new referring domain
 * that matches one of these is worth a relationship, so it lands on
 * "TO CONTACT" instead of the generic "NEW" triage bucket.
 */
const PILLAR_RULES: { rule: string; test: RegExp }[] = [
  { rule: "Pillar: Guest Post / Write-for-Us", test: /(guestpost|writeforus|contributor|blog|magazine|journal|press|news|media)/i },
  { rule: "Pillar: Bathroom / Sanitary Niche", test: /(bath|toilet|bidet|sanitar|shower|washroom|plumb|ceramic|kb|kitchen)/i },
  { rule: "Pillar: Architecture & Design", test: /(archi|design|interior|decor|studio|render|build|construct|specifi|home|house|renovat|remodel)/i },
  { rule: "Pillar: B2B Directory / Marketplace", test: /(directory|listing|marketplace|supplier|manufactur|wholesale|trade|export|import|b2b|europages|accio)/i },
  { rule: "Pillar: Hospitality / Luxury", test: /(hotel|hospitality|resort|spa|luxur|villa|yacht|estate|property|realty)/i },
  { rule: "Pillar: Institutional (.edu/.gov/.org)", test: /\.(edu|gov|ac\.[a-z]{2}|org)$/i },
];

export interface Classification {
  status: GSCReferringDomain["status"];
  rule: string;
}

/** Applies the strategy pillar rules to a newly detected referring domain. */
export function classifyNewDomain(domain: string, links: number): Classification {
  if (TOXIC_DOMAINS.includes(domain))
    return { status: "IGNORED", rule: "Auto-disavowed: on toxic PBN list" };
  if (SPAM_TLDS.test(domain))
    return { status: "IGNORED", rule: "Auto-flagged: spam-pattern TLD" };

  for (const { rule, test } of PILLAR_RULES) {
    if (test.test(domain)) return { status: "TO CONTACT", rule };
  }

  if (links >= 10)
    return { status: "TO CONTACT", rule: "Pillar: High link volume (10+ links)" };

  return { status: "NEW", rule: "No pillar match — manual triage" };
}


/** Minimal RFC-4180 CSV parser (handles quoted fields and embedded commas). */
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
      continue;
    }
    if (c === '"') inQuotes = true;
    else if (c === "," || c === "\t" || c === ";") {
      row.push(field);
      field = "";
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else if (c !== "\r") field += c;
  }
  row.push(field);
  if (row.some((f) => f.trim() !== "")) rows.push(row);
  return rows.filter((r) => r.some((f) => f.trim() !== ""));
}

function normaliseDomain(raw: string): string {
  return raw
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/.*$/, "")
    .toLowerCase();
}

type ImportResult = { kind: "domains" | "anchors" | "unknown"; added: number; updated: number; rows: number };

/**
 * Detects whether a GSC export is "Top linking sites" (domain + link count)
 * or "Top linking text" (anchor text + count) and merges it into the store.
 */
function mergeExport(store: Store, rows: string[][], today: string): { store: Store; result: ImportResult } {
  if (rows.length === 0) return { store, result: { kind: "unknown", added: 0, updated: 0, rows: 0 } };

  const header = rows[0].map((h) => h.trim().toLowerCase());
  const body = /site|domain|target|link|text|anchor|page/.test(header.join(" ")) ? rows.slice(1) : rows;

  const anchorHeader = header.some((h) => h.includes("text") || h.includes("anchor"));
  const domainHeader = header.some((h) => h.includes("site") || h.includes("domain"));

  // Fall back to shape detection when the header is localised.
  const firstCell = body[0]?.[0]?.trim() ?? "";
  const looksLikeDomain = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+/.test(firstCell);
  const kind: ImportResult["kind"] = anchorHeader
    ? "anchors"
    : domainHeader || looksLikeDomain
      ? "domains"
      : "anchors";

  let added = 0;
  let updated = 0;
  const next: Store = {
    ...store,
    domains: { ...store.domains },
    anchors: { ...store.anchors },
  };

  for (const r of body) {
    const label = (r[0] ?? "").trim();
    if (!label) continue;
    const count = Number(String(r[1] ?? "").replace(/[^\d]/g, "")) || 0;

    if (kind === "domains") {
      const domain = normaliseDomain(label);
      if (!domain || !domain.includes(".")) continue;
      const existing = next.domains[domain];
      if (existing) {
        next.domains[domain] = { ...existing, links: count || existing.links, lastSeen: today };
        updated++;
      } else {
        const c = classifyNewDomain(domain, count);
        next.domains[domain] = {
          domain,
          links: count,
          firstSeen: today,
          lastSeen: today,
          status: c.status,
          rule: c.rule,
        };

        added++;
      }
    } else {
      const anchor = label.slice(0, 200);
      const existing = next.anchors[anchor];
      if (existing) {
        next.anchors[anchor] = { anchor, count: count || existing.count, lastSeen: today };
        updated++;
      } else {
        next.anchors[anchor] = { anchor, count, lastSeen: today };
        added++;
      }
    }
  }

  next.lastImport = today;
  return { store: next, result: { kind, added, updated, rows: body.length } };
}

const statusClass = (s: GSCReferringDomain["status"]) =>
  s === "NEW"
    ? "bg-blue-500/20 text-blue-300 border-blue-500/40"
    : s === "TO CONTACT"
      ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
      : s === "REVIEWED"
        ? "bg-slate-500/20 text-slate-300 border-slate-500/40"
        : s === "OUTREACH"
          ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
          : "bg-red-500/20 text-red-300 border-red-500/40";

export default function GSCImportPanel() {
  const [store, setStore] = useState<Store>(EMPTY);
  const [message, setMessage] = useState<string | null>(null);
  const [view, setView] = useState<"domains" | "anchors">("domains");
  const [query, setQuery] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setStore({ ...EMPTY, ...JSON.parse(raw) });
    } catch {
      /* ignore corrupted store */
    }
  }, []);

  const persist = (next: Store) => {
    setStore(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* quota */
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const today = new Date().toISOString().slice(0, 10);
    let working = store;
    const summaries: string[] = [];

    for (const file of Array.from(files)) {
      const text = await file.text();
      const { store: merged, result } = mergeExport(working, parseCSV(text), today);
      working = merged;
      summaries.push(
        `${file.name}: ${result.added} new ${result.kind}, ${result.updated} updated (${result.rows} rows)`
      );
    }

    persist(working);
    setMessage(summaries.join(" · "));
    if (fileRef.current) fileRef.current.value = "";
  };

  const cycleStatus = (domain: string) => {
    const d = store.domains[domain];
    if (!d) return;
    persist({ ...store, domains: { ...store.domains, [domain]: { ...d, status: STATUS_CYCLE[d.status] } } });
  };

  const clearAll = () => {
    if (!confirm("Clear all imported Search Console link data?")) return;
    persist(EMPTY);
    setMessage(null);
  };

  const domains = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.values(store.domains)
      .filter((d) => !q || d.domain.includes(q))
      .sort((a, b) => b.links - a.links || a.domain.localeCompare(b.domain));
  }, [store.domains, query]);

  const anchors = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.values(store.anchors)
      .filter((a) => !q || a.anchor.toLowerCase().includes(q))
      .sort((a, b) => b.count - a.count);
  }, [store.anchors, query]);

  const newCount = domains.filter((d) => d.status === "NEW").length;
  const toContactCount = domains.filter((d) => d.status === "TO CONTACT").length;

  const exportCSV = () => {
    const escape = (v: unknown) => `"${String(v).replace(/"/g, '""')}"`;
    const csv =
      view === "domains"
        ? [
            ["Referring Domain", "Links", "First Seen", "Last Seen", "Status", "Auto Rule"].map(escape).join(","),
            ...domains.map((d) =>
              [d.domain, d.links, d.firstSeen, d.lastSeen, d.status, d.rule ?? ""].map(escape).join(",")
            ),
          ].join("\n")
        : [
            ["Anchor Text", "Count", "Last Seen"].map(escape).join(","),
            ...anchors.map((a) => [a.anchor, a.count, a.lastSeen].map(escape).join(",")),
          ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gsc-${view}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Import card */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-white">Import from Google Search Console</h2>
            <p className="text-slate-400 text-sm mt-1 leading-relaxed">
              Google's Search Console API does not expose the Links report, so referring domains and anchor text
              come from the report export. Open{" "}
              <a
                href="https://search.google.com/search-console/links"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 underline"
              >
                Search Console → Links
              </a>
              , click <strong className="text-slate-200">Export</strong> on{" "}
              <em>Top linking sites</em> and <em>Top linking text</em>, then drop the CSV files here. Repeat imports
              merge automatically — existing rows update, and each newly detected referring domain is auto-classified
              against the strategy pillars: relevant prospects land on{" "}
              <span className="text-emerald-300 font-semibold">TO CONTACT</span>, toxic/spam patterns on{" "}
              <span className="text-red-300 font-semibold">IGNORED</span>, everything else stays{" "}
              <span className="text-blue-300 font-semibold">NEW</span> for manual triage.
            </p>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button
              onClick={() => fileRef.current?.click()}
              className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-5 py-2.5 rounded-md transition shadow-lg shadow-amber-600/20"
            >
              Import GSC CSV
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <button
              onClick={exportCSV}
              disabled={domains.length === 0 && anchors.length === 0}
              className="border border-slate-700 hover:border-slate-500 disabled:opacity-40 text-slate-200 text-sm px-5 py-2 rounded-md transition"
            >
              Export merged CSV
            </button>
          </div>
        </div>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            void handleFiles(e.dataTransfer.files);
          }}
          className="mt-4 border-2 border-dashed border-slate-700 rounded-lg p-6 text-center text-sm text-slate-500 hover:border-amber-600/60 transition"
        >
          Drag &amp; drop your <code className="text-amber-300">Top linking sites</code> and{" "}
          <code className="text-amber-300">Top linking text</code> CSV exports here
        </div>

        {message && (
          <p className="mt-3 text-sm text-emerald-300 bg-emerald-950/30 border border-emerald-800/40 rounded px-3 py-2">
            {message}
          </p>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-5">
          {[
            { label: "Referring Domains", value: Object.keys(store.domains).length, color: "text-white" },
            { label: "To Contact", value: toContactCount, color: "text-emerald-400" },
            { label: "New (Triage)", value: newCount, color: "text-blue-400" },
            { label: "Anchor Texts", value: Object.keys(store.anchors).length, color: "text-amber-400" },
            { label: "Last Import", value: store.lastImport ?? "—", color: "text-slate-300" },
          ].map((s) => (
            <div key={s.label} className="bg-slate-950/60 border border-slate-800 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400">{s.label}</div>
              <div className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-slate-950 border border-slate-800 rounded-md p-1">
          {(["domains", "anchors"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-1.5 text-sm font-semibold rounded transition ${
                view === v ? "bg-amber-600 text-white" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {v === "domains" ? `Referring Domains (${domains.length})` : `Anchor Text (${anchors.length})`}
            </button>
          ))}
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={view === "domains" ? "Search domains…" : "Search anchors…"}
          className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500 flex-1 min-w-[200px]"
        />
        {(domains.length > 0 || anchors.length > 0) && (
          <button onClick={clearAll} className="text-xs text-red-400 hover:text-red-300 underline">
            Clear imported data
          </button>
        )}
      </div>

      {/* Tables */}
      {view === "domains" ? (
        domains.length === 0 ? (
          <p className="text-slate-500 text-sm py-8 text-center">
            No referring domains imported yet — export “Top linking sites” from Search Console and drop it above.
          </p>
        ) : (
          <div className="overflow-x-auto border border-slate-800 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Referring Domain</th>
                  <th className="text-right px-4 py-3">Links</th>
                  <th className="text-left px-4 py-3">First Seen</th>
                  <th className="text-left px-4 py-3">Last Seen</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {domains.map((d) => (
                  <tr key={d.domain} className="border-t border-slate-800 hover:bg-slate-900/40">
                    <td className="px-4 py-3">
                      <a
                        href={`https://${d.domain}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-amber-300 hover:text-amber-200 font-mono"
                      >
                        {d.domain}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-200">{d.links.toLocaleString()}</td>
                    <td className="px-4 py-3 text-slate-400">{d.firstSeen}</td>
                    <td className="px-4 py-3 text-slate-400">{d.lastSeen}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => cycleStatus(d.domain)}
                        title={d.rule ? `Auto-classified — ${d.rule}` : undefined}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${statusClass(d.status)}`}
                      >
                        {d.status}
                      </button>
                      {d.rule && <div className="text-[11px] text-slate-500 mt-1">{d.rule}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : anchors.length === 0 ? (
        <p className="text-slate-500 text-sm py-8 text-center">
          No anchor text imported yet — export “Top linking text” from Search Console and drop it above.
        </p>
      ) : (
        <div className="overflow-x-auto border border-slate-800 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Anchor Text</th>
                <th className="text-right px-4 py-3">Occurrences</th>
                <th className="text-left px-4 py-3">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {anchors.map((a) => (
                <tr key={a.anchor} className="border-t border-slate-800 hover:bg-slate-900/40">
                  <td className="px-4 py-3 text-slate-200">{a.anchor}</td>
                  <td className="px-4 py-3 text-right text-slate-200">{a.count.toLocaleString()}</td>
                  <td className="px-4 py-3 text-slate-400">{a.lastSeen}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
